import React, { useCallback, useEffect } from 'react';
import PrepopulateFields from 'helpers/prepopulateFields';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useTypedSelector, useAppDispatch } from 'combineReducers';
import { getZuoraSignature } from 'actions/zuora';
import { getPaymentMethodDetails } from 'actions/paymentMethods';
import { PaymentMethodType, ZuoraIframe } from 'interfaces/index';
import { Loading } from '@pec/aion-ui-components/components/Loading';

interface Props {
  methodType: PaymentMethodType;
  prepopulateFields?: PrepopulateFields | null;
  accountId: string;
}

const ZuoraComponent = ({ prepopulateFields, methodType, accountId }: Props): JSX.Element => {
  const { isFetching, zuoraIframeSignature } = useTypedSelector(state => state.zuora);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const zuoraCallback = useCallback(
    async responseZuora => {
      if (responseZuora.success === 'true' && responseZuora.responseFrom === 'Response_From_Submit_Page') {
        await dispatch(getPaymentMethodDetails(responseZuora.refId));
      }
      if (responseZuora.success === false && responseZuora.responseFrom !== 'Response_From_Submit_Page') {
        if (responseZuora.errorCode === 'Invalid_TimeOut' || responseZuora.errorCode === 'Attempt_Exceed_Limitation') {
          dispatch(getZuoraSignature(methodType));
        }
      }
    },
    [dispatch, methodType]
  );

  const initZ = useCallback(() => {
    if (zuoraIframeSignature?.[methodType]) {
      (window as ZuoraIframe).Z.render(
        {
          locale: 'en_US',
          style: 'inline',
          submitEnabled: 'true',
          field_accountId: accountId,
          ...zuoraIframeSignature?.[methodType]
        },
        prepopulateFields ? prepopulateFields : {},
        zuoraCallback
      );
    }
  }, [prepopulateFields, zuoraCallback, methodType, zuoraIframeSignature, accountId]);

  useEffect(() => {
    dispatch(getZuoraSignature(methodType));
  }, [dispatch, methodType]);

  useEffect(() => {
    initZ();
  }, [initZ]);

  return (
    <div className={classes.zuoraIframeContainer}>
      {isFetching && (
        <div className={classes.loadingContainer} data-testid="loading-container">
          <Loading />
        </div>
      )}
      <div id="zuora_payment" className={classes.zuoraBlock} data-testid="zuora-block"></div>
    </div>
  );
};

export default ZuoraComponent;

const useStyles = makeStyles(() =>
  createStyles({
    zuoraBlock: {
      width: '100%',
      height: '850px',
      overflow: 'scroll'
    },
    zuoraIframeContainer: {
      position: 'relative'
    },
    loadingContainer: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      background: 'rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  })
);
